// pages/strategic-playbook/tools/index.js
// ════════════════════════════════════════════════════
// STRATEGIC INTELLIGENCE — Tools landing (v1.5)
// ════════════════════════════════════════════════════
// Calculator + generator catalog. Each tool ties to one
// or more dossiers. Tools encode the framework, not
// generic calculators.
// ════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';

const TOOLS = [
  // Tier 1 — Decision-sizing
  {
    id: 'position-sizer',
    category: 'Decision Sizing',
    name: 'Position Sizer',
    subtitle: 'Kelly Criterion + Conviction Tier',
    description:
      'Calculate the right position size based on edge probability, win/loss ratio, conviction tier, and account size. Half-Kelly + conviction-tier cap applied.',
    connectsTo: 'Buffett (circle of competence) · Ackman (concentrated conviction)',
    built: true,
    accent: 'aged-brass',
  },
  {
    id: 'asymmetry',
    category: 'Decision Sizing',
    name: 'Asymmetry Calculator',
    subtitle: 'Is this bet worth taking?',
    description:
      'Calculate expected value + risk/reward ratio. Verdict: "Asymmetric (3:1 reward/risk)" or "Symmetric — pass." Forces probability-weighted thinking.',
    connectsTo: 'Thiel (rare contrarian truths)',
    built: true,
    accent: 'plum',
  },
  {
    id: 'compound-forecaster',
    category: 'Decision Sizing',
    name: 'Compound Forecaster',
    subtitle: 'The most powerful force',
    description:
      'Project compounding over decades. Side-by-side: your trading vs index. Forces the long-term math Munger calls "the most powerful force in the universe."',
    connectsTo: 'Buffett · Munger',
    built: true,
    accent: 'forest-moss',
  },
  {
    id: 'barbell-allocator',
    category: 'Decision Sizing',
    name: 'Barbell Allocator',
    subtitle: 'Taleb 80/20 split',
    description:
      'Antifragile capital allocation: 80% extreme-safe + 20% extreme-risk. Avoids the middle-risk trap.',
    connectsTo: 'Taleb (antifragile)',
    built: true,
    accent: 'cognac',
  },
  {
    id: 'drawdown-test',
    category: 'Decision Sizing',
    name: 'Drawdown Tolerance Test',
    subtitle: 'Honest psychological capacity',
    description:
      'Position too big? At what drawdown would you panic-sell? Reverse-engineers the right size from your actual psychological tolerance.',
    connectsTo: 'Marks (cycles) · Resilience pillar',
    built: true,
    accent: 'oxblood',
  },

  // Tier 2 — Strategic frame generators
  {
    id: 'pre-mortem',
    category: 'Strategic Framing',
    name: 'Pre-Mortem Generator',
    subtitle: 'Munger inversion as a tool',
    description:
      'Type a planned decision → 7 failure modes ranked by probability with mitigation per mode. Forces failure-thinking before acting.',
    connectsTo: 'Munger (inversion)',
    built: true,
    accent: 'aged-bronze',
  },
  {
    id: 'cost-of-action',
    category: 'Strategic Framing',
    name: 'Cost-of-Action Calculator',
    subtitle: 'Name what you give up',
    description:
      'Surface the AND — what you give up (time, optionality, identity, relationships, capital) for any decision. One-way door warning if applicable.',
    connectsTo: 'Honest Read framework (every dossier)',
    built: true,
    accent: 'sage-olive',
  },
  {
    id: 'what-would-thinker-do',
    category: 'Strategic Framing',
    name: 'What Would [Thinker] Do',
    subtitle: 'Apply any thinker to your situation',
    description:
      'Pick Buffett, Thiel, Ackman, Munger, Marks, Naval, Taleb, or Bezos. Describe situation. Get their lens applied with concrete next step.',
    connectsTo: 'All 8 thinker dossiers',
    built: true,
    accent: 'aged-brass',
  },
  {
    id: 'cycle-position',
    category: 'Strategic Framing',
    name: 'Cycle-Position Indicator',
    subtitle: 'Howard Marks framework',
    description:
      'Where are we in the cycle? Input 5 indicators (credit spreads, sentiment, IPO volume, etc.). Verdict: late expansion / contraction / bottom. Defensive vs aggressive recommendation.',
    connectsTo: 'Marks (second-level thinking)',
    built: true,
    accent: 'deep-teal',
  },
  {
    id: 'cascade-mapper',
    category: 'Strategic Framing',
    name: 'Cascade Mapper',
    subtitle: 'Map any shock to your life',
    description:
      'Input a shock or event + your personal exposure → customized 10-layer cascade map. How does this hit YOUR career, business, portfolio, family?',
    connectsTo: 'All 8 shock playbooks',
    built: true,
    accent: 'oxblood',
  },

  // Tier 3 — Self-assessment (after Decision Log)
  {
    id: 'conviction-calibrator',
    category: 'Self-Assessment',
    name: 'Conviction Calibrator',
    subtitle: 'Are your high-conviction bets actually high probability?',
    description:
      'After 6+ months of Decision Log entries, see: "Your high-conviction wins 67%; probes 42%. Overconfident on real bets by 15pp."',
    connectsTo: 'Decision Log (prerequisite)',
    built: false,
    accent: 'plum',
  },
  {
    id: 'pillar-heatmap',
    category: 'Self-Assessment',
    name: 'Personal Pillar Heatmap',
    subtitle: 'Which pillars do you keep hitting?',
    description:
      'Visual map of which of the 8 Pillars your decisions keep touching. P6 Resilience appearing 47% = your weak spot.',
    connectsTo: 'Decision Log',
    built: false,
    accent: 'forest-moss',
  },
  {
    id: 'time-horizon-filter',
    category: 'Self-Assessment',
    name: 'Time-Horizon Filter',
    subtitle: 'Filter dossiers to YOUR horizon',
    description:
      'Available time + decision type → which dossiers apply. Filters out wrong-horizon noise (Buffett 30y vs Thiel 7y vs Ackman 3y).',
    connectsTo: 'All dossiers',
    built: false,
    accent: 'aged-bronze',
  },
];

const CATEGORIES = [
  { id: 'Decision Sizing', label: 'Decision Sizing — pure math + verdicts' },
  { id: 'Strategic Framing', label: 'Strategic Framing — frame generators' },
  { id: 'Self-Assessment', label: 'Self-Assessment — personal calibration' },
];

export default function ToolsLanding() {
  const builtCount = TOOLS.filter((t) => t.built).length;

  return (
    <div className="sp-root">
      <Head>
        <title>Tools · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <div className="sp-topbar">
          <div>
            <h1>Strategic Intelligence</h1>
            <div className="sp-topbar-meta">
              Tools · {builtCount} of {TOOLS.length} live
            </div>
          </div>
          <nav className="sp-topbar-nav">
            <Link href="/strategic-playbook">Dashboard</Link>
            <Link href="/strategic-playbook/library">Library</Link>
            <Link href="/strategic-playbook/tools" className="sp-active">
              Tools
            </Link>
            <Link href="/strategic-playbook/decisions">Decisions</Link>
            <Link href="/strategic-playbook/simulate">Simulate</Link>
          </nav>
        </div>

        {/* Hero */}
        <div
          style={{
            marginBottom: 36,
            padding: '24px 0',
            borderBottom: '1px solid var(--sp-divider)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.20em',
              color: 'var(--sp-aged-brass)',
              marginBottom: 14,
            }}
          >
            Calculators + Frame Generators
          </div>
          <h2
            style={{
              fontFamily: 'var(--sp-font-serif)',
              fontWeight: 500,
              fontSize: 28,
              lineHeight: 1.25,
              margin: '0 0 14px 0',
              color: 'var(--sp-warm-cream)',
              letterSpacing: '-0.005em',
            }}
          >
            Tools turn the dossiers into something you RUN, not just read.
          </h2>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.7,
              color: 'rgba(232, 221, 196, 0.78)',
              maxWidth: 720,
              margin: 0,
            }}
          >
            Each tool ties to one or more dossiers and applies the framework on demand.
            Position Sizer is built on Buffett+Ackman discipline. Asymmetry Calculator
            forces Thiel-style probability thinking. Pre-Mortem Generator is Munger
            inversion as a one-click tool. Encoding the framework, not generic math.
          </p>
        </div>

        {/* Tools grouped by category */}
        {CATEGORIES.map((cat) => {
          const inCat = TOOLS.filter((t) => t.category === cat.id);
          if (inCat.length === 0) return null;

          return (
            <div key={cat.id} style={{ marginBottom: 40 }}>
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.20em',
                  color: 'var(--sp-aged-brass)',
                  borderTop: '1px solid var(--sp-aged-brass)',
                  paddingTop: 12,
                  marginBottom: 18,
                }}
              >
                {cat.label}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                  gap: 16,
                }}
              >
                {inCat.map((tool) => (
                  <ToolCard key={tool.id} tool={tool} />
                ))}
              </div>
            </div>
          );
        })}

        <div className="sp-footer-mark">
          — {builtCount} live · {TOOLS.length - builtCount} queued · each tied to a dossier —
        </div>
      </div>
    </div>
  );
}

function ToolCard({ tool }) {
  const accentVar = `var(--sp-${tool.accent})`;
  const inner = (
    <>
      <div
        style={{
          height: 3,
          background: accentVar,
          opacity: tool.built ? 0.75 : 0.25,
        }}
      />
      <div style={{ padding: '24px 26px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.18em',
              color: tool.built ? 'var(--sp-aged-brass)' : 'rgba(232, 221, 196, 0.40)',
            }}
          >
            {tool.subtitle}
          </div>
          {!tool.built && (
            <span
              style={{
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 9,
                color: 'rgba(232, 221, 196, 0.40)',
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                padding: '3px 8px',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius)',
              }}
            >
              Soon
            </span>
          )}
        </div>
        <h3
          style={{
            fontFamily: 'var(--sp-font-serif)',
            fontWeight: 500,
            fontSize: 22,
            lineHeight: 1.25,
            margin: '0 0 12px 0',
            color: tool.built ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.50)',
            letterSpacing: '-0.005em',
          }}
        >
          {tool.name}
        </h3>
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.6,
            color: tool.built ? 'rgba(232, 221, 196, 0.78)' : 'rgba(232, 221, 196, 0.45)',
            margin: '0 0 16px 0',
          }}
        >
          {tool.description}
        </p>
        <div
          style={{
            paddingTop: 12,
            borderTop: '1px solid var(--sp-divider)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 9,
              color: 'rgba(232, 221, 196, 0.50)',
              letterSpacing: '0.06em',
              flex: 1,
            }}
          >
            {tool.connectsTo}
          </span>
          {tool.built && (
            <span
              style={{
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 10,
                color: 'var(--sp-aged-brass)',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              Run →
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (tool.built) {
    return (
      <Link
        href={`/strategic-playbook/tools/${tool.id}`}
        className="sp-dossier-card"
        style={{ padding: 0, overflow: 'hidden' }}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      className="sp-dossier-card"
      style={{
        padding: 0,
        overflow: 'hidden',
        cursor: 'default',
        opacity: 0.78,
      }}
    >
      {inner}
    </div>
  );
}
